import { GQL } from '@aline/graphql';
import { omit } from 'ramda';
import type { Location, NodeEntry, Point, Range } from 'slate';
import { Editor, Node, Path, Text, Transforms } from 'slate';

import { checkboxStateText } from '../../constants';
import type { CheckboxStateText } from '../../constants';
import type { UUID } from '../../types/type-helpers';
import { mergeWithDefaults } from '../../util';
import type {
  AlineText,
  AlineTextFormats,
  CheckboxElement,
  CheckboxOptionFieldElement,
  InputFieldElement,
  InsertEdit,
  NonSigFieldElement,
  ParagraphElement,
  ProcessTemplateField,
  SignatureFieldElement,
  SignatureFieldProps,
} from '../internal';
import {
  AlineTransforms,
  createCheckboxOptionFieldElement,
  createDefaultText,
  createParagraphWithChildren,
  EditingMode,
  getCheckboxCheckedState,
  getCheckboxInfo,
  getCheckboxInitialValue,
  getEffectiveCheckboxStateText,
  getToggleCheckboxEdit,
  inputFieldType,
  isCheckboxElement,
  isCheckboxOptionFieldElement,
  isDynamicElement,
  isInputFieldElement,
  isParagraphElement,
  isSignatureFieldElement,
  normalizeCheckboxOptionLabelText,
  Queries,
  toParagraphNodeProps,
} from '../internal';

interface CreateInputFieldElement {
  authorId: string;
  editingMode: EditingMode;
  props:
    | Pick<Exclude<NonSigFieldElement, CheckboxOptionFieldElement>, 'fieldId' | 'variant'>
    | Pick<SignatureFieldProps, 'fieldId' | 'variant'>;
  text: string;
  textFormats: AlineTextFormats;
}

export function createInputFieldElement({
  authorId,
  editingMode,
  props,
  text,
  textFormats,
}: CreateInputFieldElement): InputFieldElement {
  switch (props.variant) {
    case GQL.FieldVariant.Signature:
      return createSignatureFieldElement({
        authorId,
        editingMode,
        props,
        textFormats,
      });
    default:
      return createInputFieldNode({
        authorId,
        editingMode,
        props,
        text,
        textFormats,
      });
  }
}

interface CreateSignatureFieldElement {
  authorId: string;
  editingMode: EditingMode;
  props: Pick<
    SignatureFieldProps,
    'fieldId' | 'variant' | 'imgHeightPx' | 'imgWidthPx' | 'signatureImgId'
  >;
  textFormats: AlineTextFormats;
}

interface CreateInputFieldNode {
  authorId: string;
  editingMode: EditingMode;
  props: Pick<Exclude<NonSigFieldElement, CheckboxOptionFieldElement>, 'fieldId' | 'variant'>;
  text: string;
  textFormats: AlineTextFormats;
}
/**
 * Utility function for creating a signature field.
 */
export function createSignatureFieldElement({
  authorId,
  editingMode,
  props,
  textFormats,
}: CreateSignatureFieldElement): SignatureFieldElement {
  const { fieldId, imgHeightPx, imgWidthPx, signatureImgId, variant } = props;
  const insertEdit: InsertEdit = {
    authorId,
    type: 'insertNode',
    value: {},
  };

  return {
    children: [
      {
        ...textFormats,
        comments: [],
        suggestedEdits: editingMode === EditingMode.SUGGESTING ? [insertEdit] : [],
        text: '',
      },
    ],
    fieldId,
    formats: textFormats,
    imgHeightPx,
    imgWidthPx,
    signatureImgId,
    suggestedEdits: [],
    type: inputFieldType,
    variant,
  };
}

export function createInputFieldNode({
  authorId,
  editingMode,
  props,
  text,
  textFormats,
}: CreateInputFieldNode): Exclude<NonSigFieldElement, CheckboxOptionFieldElement> {
  const insertEdit: InsertEdit = {
    authorId,
    type: 'insertNode',
    value: {},
  };
  return {
    children: [
      {
        ...textFormats,
        comments: [],
        suggestedEdits: editingMode === EditingMode.SUGGESTING ? [insertEdit] : [],
        text,
      },
    ],
    fieldId: props.fieldId,
    formats: textFormats,
    suggestedEdits: [],
    type: inputFieldType,
    variant: props.variant,
  };
}

const FieldTransforms = {
  /**
   * Clear all signatures from a Document.
   */
  clearSignatures: (editor: Editor): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      AlineTransforms.setSigFieldProps(editor, {
        at: [],
        props: { imgHeightPx: null, imgWidthPx: null, signatureImgId: null },
      });
    });
  },
  /**
   * Copies an input field.
   */
  copyInputField: (
    editor: Editor,
    options: { authorId: string; editingMode: EditingMode; fieldId: UUID; at?: Range }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, fieldId } = options;
      if (!at) return;

      const resolvedRange = AlineTransforms.nudgeRangeToEdges(editor, { at });
      const inputFieldEntries = Editor.nodes<InputFieldElement>(editor, {
        at: [],
        match: node => isInputFieldElement(node) && !!node.fieldId && node.fieldId === fieldId,
        voids: true,
      });
      const firstInputFieldEntry = inputFieldEntries.next().value;
      if (!firstInputFieldEntry) return;

      const [firstExistingInputField, firstExistingInputFieldPath] = firstInputFieldEntry;
      const textFormats = getFieldFormats(editor, resolvedRange);
      const insertEdit: InsertEdit = {
        authorId,
        type: 'insertNode',
        value: {},
      };
      let copiedInputField: InputFieldElement;

      if (isCheckboxOptionFieldElement(firstExistingInputField)) {
        const { isChecked, labelText } = getCheckboxInfo([
          firstExistingInputField,
          firstExistingInputFieldPath,
        ]);

        copiedInputField = createCheckboxOptionFieldElement({
          checkboxOptionProps: {
            fieldId: firstExistingInputField.fieldId,
            id: firstExistingInputField.id,
          },
          isChecked,
          text: labelText,
          textProps: {
            ...textFormats,
            comments: [],
            suggestedEdits: editingMode === EditingMode.SUGGESTING ? [insertEdit] : [],
          },
        });
      } else {
        const inputFieldText = Queries.getNodeText(editor, firstExistingInputFieldPath);

        // Note: Spread ...textFormats first as we want to override some props (e.g., suggestedEdits and comments).
        copiedInputField = {
          ...firstExistingInputField,
          children: [
            {
              ...textFormats,
              comments: [],
              suggestedEdits: editingMode === EditingMode.SUGGESTING ? [insertEdit] : [],
              text: inputFieldText,
            },
          ],
          formats: textFormats,
        };
      }
      const locationAfterUnwrap = AlineTransforms.unwrapInlines(editor, {
        at: resolvedRange,
        editingMode,
        voids: true,
      });
      if (!locationAfterUnwrap) return;

      Transforms.insertNodes(editor, copiedInputField, {
        at: locationAfterUnwrap,
        select: true,
        voids: true,
      });
    });
  },

  /**
   * Deletes all occurrences of an input field in a document.  For single deletes, the user can simply backspace and delete a single instance of an input field.
   */
  deleteInputFieldInstances: (
    editor: Editor,
    options: { editingMode: EditingMode; fieldId: UUID }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { fieldId } = options;

      Transforms.removeNodes(editor, {
        at: [],
        match: node => isDynamicElement(node) && node.fieldId === fieldId,
        voids: true,
      });
    });
  },

  /**
   * Initializes an input field to its default value - i.e., its default or label.
   */
  initializeInputField: (
    editor: Editor,
    options: {
      authorId: string;
      fields: Record<UUID, ProcessTemplateField>;
      inputFieldEntry: NodeEntry<InputFieldElement>;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, fields, inputFieldEntry } = options;
      const [inputFieldElement] = inputFieldEntry;

      if (isCheckboxOptionFieldElement(inputFieldElement)) {
        const checkboxInitialValue = getCheckboxInitialValue(fields[inputFieldElement.fieldId]);
        if (!checkboxInitialValue) return;

        const { answerOptions, selectedAnswerIds } = checkboxInitialValue;

        /**
         * Never pass `text` for a checkbox option: its range holds a nested checkbox plus a
         * label, and replacing that with text destroys the structure.
         */
        AlineTransforms.updateInputField(editor, {
          answerOptionsInput: answerOptions,
          assigneeId: null,
          authorId,
          editingMode: EditingMode.EDITING,
          fieldId: inputFieldElement.fieldId,
          update: selectedAnswerIds ? { checkboxSelectedAnswerIds: selectedAnswerIds } : {},
        });
        return;
      }

      switch (inputFieldElement.variant) {
        case 'SIGNATURE': {
          const allSigFieldEntries = [
            ...Editor.nodes(editor, {
              at: [],
              match: n => isSignatureFieldElement(n) && n.fieldId === inputFieldElement.fieldId,
              voids: true,
            }),
          ];
          allSigFieldEntries.forEach(([, sigFieldPath]) => {
            AlineTransforms.setSigFieldProps(editor, {
              at: sigFieldPath,
              props: { signatureImgId: null },
            });
          });
          break;
        }
        default: {
          const safeLabel = Queries.getInitializedFieldValue({
            fields,
            inputField: inputFieldElement,
          });

          // Pass assigneeId null to skip the assignee checks in this case.
          AlineTransforms.updateInputField(editor, {
            assigneeId: null,
            authorId,
            editingMode: EditingMode.EDITING,
            fieldId: inputFieldElement.fieldId,
            update: { text: safeLabel },
          });
        }
      }
    });
  },

  /** Enter in a checkbox label: split the label text, then split the paragraph. */
  insertBreakInCheckboxOptionLabel: (
    editor: Editor,
    options: { at: Point; authorId: string; editingMode: EditingMode; select?: boolean }
  ): boolean => {
    const { at, authorId, editingMode, select = true } = options;
    const ctx = getCheckboxLabelBreakContext(editor, at);
    if (!ctx) return false;

    const { paraPath } = ctx;

    AlineTransforms.withoutNormalizing(editor, () => {
      Transforms.splitNodes(editor, { always: true, at, match: Text.isText, voids: true });
      if (editingMode === EditingMode.SUGGESTING) {
        AlineTransforms.suggestBreakAtCursor(editor, { authorId, cursorPoint: at, editingMode });
      } else {
        AlineTransforms.splitParagraph(editor, { at });
      }
    });

    const nextPath = Path.next(paraPath);
    if (select) AlineTransforms.select(editor, Editor.start(editor, nextPath));

    return true;
  },

  insertCheckboxOptionFields: (
    editor: Editor,
    options: {
      answerOptions: Array<{ id: string; optionText?: string | null }>;
      authorId: string;
      editingMode: EditingMode;
      fieldId: UUID;
      at?: Range;
      select?: boolean;
      selectedAnswerIds?: string[];
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        answerOptions,
        at = editor.selection,
        authorId,
        editingMode,
        fieldId,
        select = true,
        selectedAnswerIds = [],
      } = options;
      if (!at || !answerOptions.length) return;

      const resolvedRange = AlineTransforms.nudgeRangeToEdges(editor, { at });
      const textFormats = getFieldFormats(editor, resolvedRange);
      const locationAfterUnwrap = AlineTransforms.unwrapInlines(editor, {
        at: resolvedRange,
        editingMode,
        voids: true,
      });
      if (!locationAfterUnwrap) return;
      const paragraphProps = getInsertedCheckboxParagraphProps(editor, resolvedRange);
      const checkboxOptions = createCheckboxOptionNodes({
        answerOptions,
        authorId,
        editingMode,
        fieldId,
        selectedAnswerIds,
        textProps: textFormats,
      });

      Transforms.insertNodes(editor, checkboxOptions[0], {
        at: locationAfterUnwrap,
        select: checkboxOptions.length === 1 ? select : false,
        voids: true,
      });

      if (checkboxOptions.length === 1) return;

      const paragraphEntry = Editor.above(editor, {
        at: locationAfterUnwrap,
        match: isParagraphElement,
      });
      if (!paragraphEntry) return;

      const [, paragraphPath] = paragraphEntry;
      const remainingParagraphs = checkboxOptions
        .slice(1)
        .map(option =>
          createParagraphWithChildren({ children: [option], paraProps: paragraphProps })
        );
      Transforms.insertNodes(editor, remainingParagraphs, {
        at: Path.next(paragraphPath),
        voids: true,
      });

      if (select) {
        let lastParagraphPath = paragraphPath;
        for (let i = 1; i < checkboxOptions.length; i++) {
          lastParagraphPath = Path.next(lastParagraphPath);
        }
        AlineTransforms.select(editor, Editor.end(editor, lastParagraphPath.concat(0)));
      }
    });
  },

  /**
   * Inserts an input field into the document.
   */
  insertInputField: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      props: CreateInputFieldElement['props'];
      text: string;
      at?: Range;
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, props, select = true, text } = options;
      if (!at) return;

      const resolvedRange = AlineTransforms.nudgeRangeToEdges(editor, { at });
      const textFormats = getFieldFormats(editor, resolvedRange);
      const inputField = createInputFieldElement({
        authorId,
        editingMode,
        props,
        text,
        textFormats,
      });
      const locationAfterUnwrap = AlineTransforms.unwrapInlines(editor, {
        at: resolvedRange,
        editingMode,
        voids: true,
      });
      if (!locationAfterUnwrap) return;

      Transforms.insertNodes(editor, inputField, {
        at: locationAfterUnwrap,
        select,
        voids: true,
      });
    });
  },
  /**
   * Inserts a signature field in the JSON.  Provides spacing before so the signature field renders/looks nice
   * on insertion.
   */
  insertSignatureField: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      props: CreateSignatureFieldElement['props'];
      at?: Range;
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection } = options;
      if (!at) return;

      AlineTransforms.insertInputField(editor, { ...options, at, text: '' });
    });
  },

  /**
   * Sets props on a sig field at a Location.
   */
  setSigFieldProps(
    editor: Editor,
    options: {
      props: Partial<SignatureFieldProps>;
      at?: Location;
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, props } = options;
      if (!at) return;

      const sigFieldEntries = Editor.nodes(editor, {
        at,
        match: isSignatureFieldElement,
        voids: true,
      });
      for (const [, sigFieldPath] of sigFieldEntries)
        Transforms.setNodes(editor, props, { at: sigFieldPath, voids: true });
    });
  },

  /**
   * Signs a signature field.
   */
  signOrClearSignatureField: (
    editor: Editor,
    options: {
      assigneeId: UUID;
      sigFieldPath: Path;
      signatureImgId: UUID | null;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { assigneeId, sigFieldPath, signatureImgId } = options;
      const sigField = Node.get(editor, sigFieldPath);
      const { isUserAuthorized } = Queries.isUserAuthorizedToCompleteSigField(editor, {
        assigneeId,
      });
      if (!isSignatureFieldElement(sigField) || !isUserAuthorized) return;

      AlineTransforms.setSigFieldProps(editor, {
        at: sigFieldPath,
        props: { signatureImgId },
      });
    });
  },

  toggleCheckbox(
    editor: Editor,
    options: {
      assigneeId: UUID | null;
      authorId: string;
      editingMode: EditingMode;
      optionId: string;
    }
  ) {
    const { assigneeId, authorId, editingMode, optionId } = options;
    const checkboxEntries = getCheckboxEntriesByOptionId(editor, optionId).toArray();
    const checkboxEntry = checkboxEntries[0];
    if (!checkboxEntry) throw new Error('Could not find checkbox');

    const fieldId = getCheckboxOptionFieldId(editor, optionId);
    if (fieldId) {
      const isChecked = getCheckboxSelectedAnswerIdsFromField(editor, fieldId).includes(optionId);
      const nextIds = getCheckboxSelectedAnswerIdsAfterToggle(
        editor,
        fieldId,
        optionId,
        !isChecked
      );

      AlineTransforms.updateInputField(editor, {
        assigneeId,
        authorId,
        editingMode,
        fieldId,
        update: { checkboxSelectedAnswerIds: nextIds },
      });
      return;
    }

    const [checkboxNode] = checkboxEntry;
    const isChecked = getCheckboxCheckedState(checkboxEntry);
    const textToInsert = checkboxStateText(!isChecked);
    if (getEffectiveCheckboxStateText(checkboxNode) === textToInsert) return;

    AlineTransforms.withoutNormalizing(editor, () => {
      for (const [, instanceCheckboxPath] of checkboxEntries) {
        setCheckboxStateText(editor, {
          authorId,
          checkboxPath: instanceCheckboxPath,
          editingMode,
          textToInsert,
        });
      }
    });
  },

  /**
   * Updates text or other parameters of an input field.  All instances of an
   * input field in a document are updated simultaneously.
   */
  updateInputField: (
    editor: Editor,
    options: {
      assigneeId: UUID | null;
      authorId: string;
      editingMode: EditingMode;
      fieldId: UUID;
      update: {
        checkboxSelectedAnswerIds?: string[];
        props?: Partial<InputFieldElement>;
        text?: string;
      };
      answerOptionsInput?: { id: string; legacyId?: string; optionText?: string | null }[];
      /**
       * Renames options still keyed by their AnswerOption row id without reconciling the
       * option set. Callers that only change checked state pass this instead of
       * `answerOptionsInput`, whose option list would also add, drop, and relabel options.
       */
      legacyOptionIds?: { id: string; legacyId: string }[];
    }
  ): { updated: boolean } => {
    return AlineTransforms.withoutNormalizing<{ updated: boolean }>(editor, () => {
      const updatedRecord = { updated: false };
      const {
        answerOptionsInput,
        assigneeId,
        authorId,
        editingMode,
        fieldId,
        legacyOptionIds,
        update,
      } = options;
      const { checkboxSelectedAnswerIds, props, text } = update;
      const { isUserAuthorized } = Queries.isUserAuthorizedToCompleteNonSigField(editor, {
        assigneeId,
      });
      if (!isUserAuthorized) return updatedRecord;

      const rangeRef = editor.selection ? Editor.rangeRef(editor, editor.selection) : null;
      /**
       * Rename before anything else reads option ids, so nodes are matched by the id the
       * caller uses rather than deleted and re-inserted somewhere else in the document.
       */
      const optionIdRenames = answerOptionsInput ?? legacyOptionIds;
      if (
        optionIdRenames &&
        renameLegacyCheckboxOptionIds(editor, { answerOptionsInput: optionIdRenames, fieldId })
      ) {
        updatedRecord.updated = true;
      }

      if (answerOptionsInput) {
        const nextOptionIds = new Set(answerOptionsInput.map(option => option.id));
        const optionEntries = [
          ...Editor.nodes<CheckboxOptionFieldElement>(editor, {
            at: [],
            match: node => isCheckboxOptionFieldElement(node) && node.fieldId === fieldId,
            reverse: true,
            voids: true,
          }),
        ];
        const selectedOptionIds = new Set(
          optionEntries
            .filter(optionEntry => getCheckboxInfo(optionEntry).isChecked)
            .map(([optionNode]) => optionNode.id)
        );

        for (const [optionNode, optionPath] of optionEntries) {
          if (nextOptionIds.has(optionNode.id)) continue;
          Transforms.removeNodes(editor, { at: optionPath, voids: true });
          updatedRecord.updated = true;
        }

        for (const option of answerOptionsInput) {
          const pathsForOption = [
            ...Editor.nodes<CheckboxOptionFieldElement>(editor, {
              at: [],
              match: n =>
                isCheckboxOptionFieldElement(n) && n.fieldId === fieldId && n.id === option.id,
              voids: true,
            }),
          ]
            .map(([, path]) => path)
            .sort((a, b) => Path.compare(b, a));

          for (const optionPath of pathsForOption) {
            const existingOption = Node.getIf(editor, optionPath);
            if (!existingOption || !isCheckboxOptionFieldElement(existingOption)) continue;

            const normalizedLabelText = normalizeCheckboxOptionLabelText(option.optionText);
            if (replaceCheckboxOptionLabelText(editor, optionPath, normalizedLabelText)) {
              updatedRecord.updated = true;
            }
          }
        }

        const optionPathsById = [
          ...Editor.nodes<CheckboxOptionFieldElement>(editor, {
            at: [],
            match: node => isCheckboxOptionFieldElement(node) && node.fieldId === fieldId,
            voids: true,
          }),
        ].reduce<Record<string, Path[]>>((acc, [optionNode, path]) => {
          (acc[optionNode.id] ??= []).push(path);
          return acc;
        }, {});

        let appendParagraphPath: Path | null = null;
        let groupedAppendPath: Path | null = null;
        let groupedTextProps: AlineTextFormats | undefined;
        let shouldAppendToGroupedParagraph = false;
        const remainingOptionEntries = [
          ...Editor.nodes<CheckboxOptionFieldElement>(editor, {
            at: [],
            match: node => isCheckboxOptionFieldElement(node) && node.fieldId === fieldId,
            voids: true,
          }),
        ];
        const lastEntry = remainingOptionEntries.at(-1);
        if (lastEntry) {
          const [lastOptionNode, lastOptionPath] = lastEntry;
          const lastParagraphPath = Path.parent(lastOptionPath);
          appendParagraphPath = Path.next(lastParagraphPath);

          const lastParagraph = Node.getIf(editor, lastParagraphPath);
          if (lastParagraph && isParagraphElement(lastParagraph)) {
            const optionCountInParagraph = lastParagraph.children.some(
              child => isCheckboxOptionFieldElement(child) && child.id !== lastOptionNode.id
            );

            if (optionCountInParagraph) {
              shouldAppendToGroupedParagraph = true;
              groupedAppendPath = Path.next(lastOptionPath);

              groupedTextProps = Queries.firstTextNodeFormats(editor, lastParagraphPath);
            }
          }
        }

        const appendedOptions = answerOptionsInput.filter(
          option => !optionPathsById[option.id]?.length
        );
        if (appendedOptions.length) {
          if (shouldAppendToGroupedParagraph && groupedAppendPath) {
            const groupedNodes = createCheckboxOptionNodes({
              answerOptions: appendedOptions,
              fieldId,
              selectedAnswerIds: [...selectedOptionIds],
              textProps: groupedTextProps,
            }).flatMap(option => [createDefaultText(groupedTextProps, ' '), option]);

            Transforms.insertNodes(editor, groupedNodes, {
              at: groupedAppendPath,
              voids: true,
            });
            updatedRecord.updated = true;
          } else if (appendParagraphPath) {
            const lastEntryParagraphPath = lastEntry ? Path.parent(lastEntry[1]) : null;
            const lastParagraph = lastEntryParagraphPath
              ? Node.getIf(editor, lastEntryParagraphPath)
              : undefined;
            const paragraphProps =
              lastParagraph && isParagraphElement(lastParagraph)
                ? getCopiedCheckboxParagraphProps(lastParagraph)
                : {};
            const appendedParagraphs = createCheckboxOptionNodes({
              answerOptions: appendedOptions,
              fieldId,
              selectedAnswerIds: [...selectedOptionIds],
            }).map(option =>
              createParagraphWithChildren({
                children: [option],
                paraProps: paragraphProps,
              })
            );

            Transforms.insertNodes(editor, appendedParagraphs, {
              at: appendParagraphPath,
              voids: true,
            });
            updatedRecord.updated = true;
          }
        }
      }
      if (checkboxSelectedAnswerIds) {
        const selectedOptionIds = new Set(checkboxSelectedAnswerIds);
        const optionEntries = [
          ...Editor.nodes<CheckboxOptionFieldElement>(editor, {
            at: [],
            match: node =>
              isCheckboxOptionFieldElement(node) &&
              node.fieldId === fieldId &&
              !!getCheckboxInfo([node, []]).checkboxEntry,
            voids: true,
          }),
        ];

        for (const optionEntry of optionEntries) {
          const [optionNode] = optionEntry;
          const { checkboxEntry } = getCheckboxInfo(optionEntry);
          if (!checkboxEntry) continue;

          const [, checkboxPath] = checkboxEntry;

          const nextValue = checkboxStateText(selectedOptionIds.has(optionNode.id));
          if (
            setCheckboxStateText(editor, {
              authorId,
              checkboxPath,
              editingMode,
              textToInsert: nextValue,
            })
          ) {
            updatedRecord.updated = true;
          }
        }
      }

      // Use typeof text === 'string' to allow for empty strings, which are falsy...
      if (typeof text === 'string') {
        const inputFieldEntries = [
          ...Editor.nodes<InputFieldElement>(editor, {
            at: [],
            match: node => isInputFieldElement(node) && node.fieldId === fieldId,
            voids: true,
          }),
        ];

        // Update in reverse path order in case the path of a field changes as we do updates.
        for (const [field, fieldPath] of [...inputFieldEntries].reverse()) {
          const fieldText = Node.string(field);
          if (text === fieldText) continue;

          /**
           * Hack: We've checked above that a User is at least Commenter and we don't want tracked
           * changes on field inputs.  So we hard-code EditingMode.EDITING.  Any better way?
           */
          AlineTransforms.insertText(editor, {
            at: fieldPath,
            authorId,
            editingMode: EditingMode.EDITING,
            textToInsert: text,
            voids: true,
          });

          updatedRecord.updated = true;
        }
      }

      /**
       * Since no properties on the input field are implemented in tracked changes, we don't have to
       * take tracked changes into account when setting any of those props.
       */
      if (props)
        Transforms.setNodes<InputFieldElement>(editor, props, {
          at: [],
          match: node => isInputFieldElement(node) && node.fieldId === fieldId,
          voids: true,
        });

      const range = rangeRef?.unref();
      if (!range) return updatedRecord;

      // If there was a selection beforehand, we restore it after doing the updates above (e.g., since inserting text can cause the selection to change).
      AlineTransforms.select(editor, range);

      return updatedRecord;
    });
  },
  /**
   * Similar to updateInputField, but...
   *
   * - Updates multiple fields at once, but only updates the text of the field.
   *
   * Useful for updating multiple fields such as during an integration.
   */
  updateInputFieldsWithData(
    editor: Editor,
    options: {
      assigneeId: UUID | null;
      authorId: string;
      data: Record<UUID, string>;
      editingMode: EditingMode;
    }
  ) {
    return AlineTransforms.withoutNormalizing<Record<UUID, { updated: boolean }>>(editor, () => {
      const { assigneeId, authorId, data, editingMode } = options;

      return Object.entries(data).reduce<Record<UUID, { updated: boolean }>>(
        (acc, [fieldId, text]) => {
          const updatedRecord = AlineTransforms.updateInputField(editor, {
            assigneeId,
            authorId,
            editingMode,
            fieldId,
            update: { text },
          });
          acc[fieldId] = updatedRecord;
          return acc;
        },
        {}
      );
    });
  },
};

export function getCheckboxEntriesByOptionId(
  editor: Editor,
  optionId: string
): Generator<NodeEntry<CheckboxElement>> {
  return Editor.nodes(editor, {
    at: [],
    match: (node, path): node is CheckboxElement => {
      if (!isCheckboxElement(node)) return false;
      if (node.optionId === optionId) return true;
      const parent = Node.parent(editor, path);
      return isCheckboxOptionFieldElement(parent) && parent.id === optionId;
    },
    voids: true,
  });
}

export function getCheckboxEntryByOptionId(
  editor: Editor,
  optionId: string
): NodeEntry<CheckboxElement> | undefined {
  return getCheckboxEntriesByOptionId(editor, optionId).next().value;
}

export function getCheckboxOptionFieldId(editor: Editor, optionId: string): string | null {
  const checkboxEntry = getCheckboxEntryByOptionId(editor, optionId);
  if (!checkboxEntry) return null;

  const parent = Node.parent(editor, checkboxEntry[1]);
  if (!isCheckboxOptionFieldElement(parent)) return null;

  return parent.fieldId;
}

export function getCheckboxSelectedAnswerIdsAfterToggle(
  editor: Editor,
  fieldId: string,
  toggledOptionId: string,
  selected: boolean
): string[] {
  const currentIds = getCheckboxSelectedAnswerIdsFromField(editor, fieldId);
  const merged = new Set(currentIds);
  if (selected) merged.add(toggledOptionId);
  else merged.delete(toggledOptionId);
  return [...merged];
}

/**
 * Renames checkbox option nodes that still carry an AnswerOption row id over to the option's
 * `choiceRefId`, in place so each option keeps its position in the document.
 */
function renameLegacyCheckboxOptionIds(
  editor: Editor,
  options: {
    answerOptionsInput: { id: string; legacyId?: string }[];
    fieldId: UUID;
  }
): boolean {
  const { answerOptionsInput, fieldId } = options;
  const idByLegacyId = new Map(
    answerOptionsInput.flatMap(option =>
      option.legacyId && option.legacyId !== option.id ? [[option.legacyId, option.id]] : []
    )
  );
  if (!idByLegacyId.size) return false;

  const optionEntries = [
    ...Editor.nodes<CheckboxOptionFieldElement>(editor, {
      at: [],
      match: node => isCheckboxOptionFieldElement(node) && node.fieldId === fieldId,
      voids: true,
    }),
  ];
  let renamed = false;

  for (const [optionNode, optionPath] of optionEntries) {
    const id = idByLegacyId.get(optionNode.id);
    if (!id) continue;

    const checkboxPaths = [
      ...Editor.nodes<CheckboxElement>(editor, {
        at: optionPath,
        match: isCheckboxElement,
        voids: true,
      }),
    ].map(([, checkboxPath]) => checkboxPath);

    Transforms.setNodes<CheckboxOptionFieldElement>(editor, { id }, { at: optionPath });
    for (const checkboxPath of checkboxPaths) {
      Transforms.setNodes<CheckboxElement>(editor, { optionId: id }, { at: checkboxPath });
    }
    renamed = true;
  }

  return renamed;
}

function replaceCheckboxOptionLabelText(
  editor: Editor,
  optionPath: Path,
  normalizedLabelText: string
): boolean {
  const existingOption = Node.getIf(editor, optionPath);
  if (!existingOption || !isCheckboxOptionFieldElement(existingOption)) return false;

  const { checkboxEntry, labelTextEntries } = Queries.getCheckboxFieldElements([
    existingOption,
    optionPath,
  ]);
  if (!checkboxEntry) return false;

  const currentLabelText = labelTextEntries.map(([textNode]) => textNode.text).join('');
  if (currentLabelText === normalizedLabelText) return false;

  const firstLabelNode = labelTextEntries[0]?.[0];
  for (const [, labelPath] of [...labelTextEntries].reverse()) {
    Transforms.removeNodes(editor, { at: labelPath, voids: true });
  }

  if (normalizedLabelText) {
    const checkboxIndex = checkboxEntry[1][checkboxEntry[1].length - 1]!;
    const labelTextProps = firstLabelNode ? omit(['text'], firstLabelNode) : {};
    Transforms.insertNodes(editor, createDefaultText(labelTextProps, normalizedLabelText), {
      at: optionPath.concat(checkboxIndex + 1),
      voids: true,
    });
  }

  return true;
}

function getCheckboxLabelBreakContext(editor: Editor, at: Point) {
  const optionEntry = Editor.above(editor, {
    at,
    match: isCheckboxOptionFieldElement,
    mode: 'highest',
  });
  if (!optionEntry) return null;

  const [option, optionPath] = optionEntry;
  const parent = Editor.parent(editor, optionPath);
  if (!isParagraphElement(parent[0])) return null;

  const labelText = option.children.find(
    (node): node is AlineText => Text.isText(node) && node.text.trim() !== ''
  );
  if (!labelText) return null;

  return { end: Editor.end(editor, at.path), labelText, para: parent[0], paraPath: parent[1] };
}

function getFieldFormats(editor: Editor, at: Range): AlineTextFormats {
  const paragraphProps = Queries.consistentParagraphProps(editor, { at });
  const styleSheet = Queries.activeStyleSheet(editor);
  const consistentFormats = Queries.consistentFormats(editor);

  let textFormats: AlineTextFormats = styleSheet['style-normal'].styles;

  if (paragraphProps?.variant && paragraphProps.variant in styleSheet)
    textFormats = styleSheet[paragraphProps.variant].styles;
  if (consistentFormats) textFormats = mergeWithDefaults(consistentFormats, textFormats);

  return textFormats;
}

function createCheckboxOptionNodes(options: {
  answerOptions: Array<{ id: string; optionText?: string | null }>;
  fieldId: UUID;
  authorId?: string;
  editingMode?: EditingMode;
  selectedAnswerIds?: string[];
  textProps?: AlineTextFormats;
}) {
  const {
    answerOptions,
    authorId,
    editingMode,
    fieldId,
    selectedAnswerIds = [],
    textProps,
  } = options;
  const insertEdit: InsertEdit | null =
    authorId && editingMode
      ? {
          authorId,
          type: 'insertNode',
          value: {},
        }
      : null;
  const selectedOptionIds = new Set(selectedAnswerIds);

  return answerOptions.map(answerOption =>
    createCheckboxOptionFieldElement({
      checkboxOptionProps: {
        fieldId,
        id: answerOption.id,
      },
      isChecked: selectedOptionIds.has(answerOption.id),
      text: answerOption.optionText ?? '',
      textProps: textProps
        ? {
            ...textProps,
            comments: [],
            suggestedEdits:
              editingMode === EditingMode.SUGGESTING && insertEdit ? [insertEdit] : [],
          }
        : undefined,
    })
  );
}

function setCheckboxStateText(
  editor: Editor,
  options: {
    authorId: string;
    checkboxPath: Path;
    editingMode: EditingMode;
    textToInsert: CheckboxStateText;
  }
): boolean {
  const { authorId, checkboxPath, editingMode, textToInsert } = options;
  const checkbox = Node.get(editor, checkboxPath);
  if (!isCheckboxElement(checkbox)) return false;
  const toggleEdit = getToggleCheckboxEdit(checkbox);
  const effectiveText = toggleEdit?.value.to ?? checkbox.children.at(0)?.text;
  if (effectiveText === textToInsert && !toggleEdit) {
    return false;
  }

  if (editingMode === EditingMode.SUGGESTING) {
    AlineTransforms.suggestCheckboxStateChange(editor, { authorId, checkboxPath, textToInsert });
    return true;
  }

  AlineTransforms.insertText(editor, {
    at: Editor.range(editor, checkboxPath.concat(0)),
    authorId,
    editingMode,
    select: false,
    textToInsert,
    voids: true,
  });
  return true;
}

function getInsertedCheckboxParagraphProps(editor: Editor, at: Location) {
  const {
    locationId: _locationId,
    suggestedEdits: _suggestedEdits,
    ...paragraphProps
  } = Queries.consistentParagraphProps(editor, { at });
  return toParagraphNodeProps(paragraphProps);
}

function getCopiedCheckboxParagraphProps(paragraph: ParagraphElement) {
  const {
    children: _children,
    locationId: _locationId,
    suggestedEdits: _suggestedEdits,
    ...paragraphProps
  } = paragraph;
  return paragraphProps;
}

export function getCheckboxSelectedAnswerIdsFromField(editor: Editor, fieldId: string): string[] {
  const selectedAnswerIds: string[] = [];

  const checkboxOptionNodes = Editor.nodes(editor, {
    at: [],
    match: (node): node is CheckboxOptionFieldElement =>
      isCheckboxOptionFieldElement(node) && node.fieldId === fieldId,
    voids: true,
  });

  for (const optionEntry of checkboxOptionNodes) {
    if (getCheckboxInfo(optionEntry).isChecked) selectedAnswerIds.push(optionEntry[0].id);
  }

  return selectedAnswerIds;
}

export default FieldTransforms;
